// src/components/BottomNavigation/BottomNavigation.js
import React, { useState, useEffect, forwardRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, Bot, CircleUserRound } from 'lucide-react';
import { AffinRecordsIcon, GroupIcon } from '../icons';
import { useGroupNotifications } from '../../features/groups';
import { getUnreadNotificationsCount } from '../../services/notificationService';
import { useFriendRequestsContext } from '../../contexts/FriendRequestsContext';
import './BottomNavigation.css';

const BottomNavigation = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [showIndicator, setShowIndicator] = useState(false);
  const { unreadGroupCount } = useGroupNotifications();
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const { count: pendingFriendRequestCount } = useFriendRequestsContext();

  useEffect(() => {
    setShowIndicator(true);
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;

    const fetchUnreadCount = async () => {
      try {
        const count = await getUnreadNotificationsCount();
        if (isMounted) {
          setUnreadNotificationsCount(count);
        }
      } catch (error) {
        console.error('Error fetching unread notifications count:', error);
      }
    };

    fetchUnreadCount();
    const timer = setInterval(fetchUnreadCount, 60000);

    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const isNotificationsRoute =
      location.pathname === '/notifications' ||
      location.pathname.startsWith('/notifications/');

    if (isNotificationsRoute) {
      setUnreadNotificationsCount(0);
    }
  }, [location.pathname]);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleNavigate = () => {
    setShowIndicator(false);
  };

  return (
    <nav ref={ref} className="bottom-navigation">
      <div className="bottom-navigation-container">
        <Link 
          to="/records" 
          className={isActive('/records') ? 'active' : ''} 
          aria-label={t('navigation.records')}
          aria-current={isActive('/records') ? 'page' : undefined}
          onClick={handleNavigate}
        >
          <div className="icon-container">
            <AffinRecordsIcon size={24} />
            {isActive('/records') && showIndicator && <div className="indicator" />}
          </div>
        </Link>

        <Link 
          to="/chat" 
          className={isActive('/chat') ? 'active' : ''} 
          aria-label={t('navigation.chat')}
          aria-current={isActive('/chat') ? 'page' : undefined}
          onClick={handleNavigate}
        >
          <div className="icon-container">
            <Bot size={24} />
            {isActive('/chat') && showIndicator && <div className="indicator" />}
          </div>
        </Link>

        <Link
          to="/groups"
          className={isActive('/groups') ? 'active' : ''}
          aria-label={t('navigation.groups')}
          aria-current={isActive('/groups') ? 'page' : undefined}
          onClick={handleNavigate}
        >
          <div className="icon-container">
            <GroupIcon size={26} />
            {unreadGroupCount > 0 && (
              <span className="bottom-navigation-badge">
                {unreadGroupCount > 99 ? '99+' : unreadGroupCount}
              </span>
            )}
            {isActive('/groups') && showIndicator && <div className="indicator" />}
          </div>
        </Link>

        <Link
          to="/notifications"
          className={isActive('/notifications') ? 'active' : ''}
          aria-label={t('navigation.notifications')}
          aria-current={isActive('/notifications') ? 'page' : undefined}
          onClick={handleNavigate}
        >
          <div className="icon-container">
            <Bell size={24} />
            {unreadNotificationsCount > 0 && !isActive('/notifications') && (
              <span className="bottom-navigation-badge">
                {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
              </span>
            )}
            {isActive('/notifications') && showIndicator && <div className="indicator" />}
          </div>
        </Link>

        <Link 
          to="/profile" 
          className={isActive('/profile') ? 'active' : ''} 
          aria-label={t('navigation.profile')}
          aria-current={isActive('/profile') ? 'page' : undefined}
          onClick={handleNavigate}
        >
          <div className="icon-container">
            <CircleUserRound size={24} />
            {pendingFriendRequestCount > 0 && !isActive('/profile') && (
              <span className="bottom-navigation-badge">
                {pendingFriendRequestCount > 99
                  ? '99+'
                  : pendingFriendRequestCount}
              </span>
            )}
            {isActive('/profile') && showIndicator && <div className="indicator" />}
          </div>
        </Link>
      </div>
    </nav>
  );
});

BottomNavigation.displayName = 'BottomNavigation';

export default BottomNavigation;
